import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePermission } from '@/lib/permissions'
import { useCreateStorageNode, useStorageRoots } from '@/hooks/useStorage'
import { useEquipmentUnits } from '@/hooks/useEquipment'
import { sanitizeCode } from '@/components/inventory/storage/LayoutControls'
import { AddStorageUnitDialog } from './AddStorageUnitDialog'
import { DeleteStorageUnitDialog } from './DeleteStorageUnitDialog'
import { ReplaceUnitDialog } from './replace/ReplaceUnitDialog'
import { CopyLayoutDialog } from '@/components/inventory/storage/CopyLayoutDialog'
import { StorageList } from '@/components/inventory/storage/list/StorageList'
import type { StorageListRow } from '@/lib/storageList'

/**
 * Storage layout configuration, on the Equipment side.
 *
 * Renders the same `StorageList` as Inventory → Storage (design note §1): one
 * list, used in both places, minus "Receive stock" — this side is
 * configuration. Opening one goes to `/equipment/storage/:unitId`, where the
 * structure tree and slot matrix have the width they need.
 */
export function EquipmentStorageTab({ focusEquipmentId }: { focusEquipmentId?: string | null }) {
  const { can } = usePermission()
  const navigate = useNavigate()
  const roots = useStorageRoots()
  const [deleting, setDeleting] = useState<StorageListRow | null>(null)
  const [replacing, setReplacing] = useState<StorageListRow | null>(null)
  const [copying, setCopying] = useState<StorageListRow | null>(null)

  /**
   * "Storage layout" on a unit's Information dialog lands on that unit.
   *
   * When the machine does not root a tree yet, it is rooted here and the wizard
   * opens on arrival — the same two steps the Add storage unit dialog performs,
   * minus the picking, because the dialog's only question is which machine and
   * the button already answered it. The code is derived from the equipment id
   * exactly as that dialog derives it.
   *
   * A machine with no layout cannot hold anything, so stopping at the list
   * would leave someone on a screen that does not mention the unit they asked
   * for. `fallbackAddOpen` is this effect's OWN dialog, separate from the one
   * `StorageList`'s "＋ Add storage unit" button opens: it exists only for the
   * one case nothing here can resolve automatically — the machine's own label
   * yields no usable code — and lets the person choose instead of silently
   * doing nothing.
   */
  const createRoot = useCreateStorageNode()
  const equipmentUnits = useEquipmentUnits()
  const [fallbackAddOpen, setFallbackAddOpen] = useState(false)
  // One attempt per machine. `roots` is invalidated by the create, so without
  // this the effect would re-enter on its own result.
  const rooting = useRef<string | null>(null)

  const rootedEquipmentIds = new Set((roots.data ?? []).map((r) => r.equipmentId))

  useEffect(() => {
    if (!focusEquipmentId || rooting.current === focusEquipmentId) return

    const match = (roots.data ?? []).find((r) => r.equipmentId === focusEquipmentId)
    if (match) {
      navigate(`/equipment/storage/${match.id}`)
      return
    }

    // Absent is only absent once the lists have actually arrived.
    if (!roots.isSuccess || !equipmentUnits.isSuccess) return
    if (!can('inventory.configure')) return

    const machine = (equipmentUnits.data ?? []).find((u) => u.id === focusEquipmentId)
    const code = machine ? sanitizeCode(machine.label) : ''
    // No usable code from the equipment id is the one case that still needs a
    // person: the dialog lets them choose and shows why.
    if (!machine || !code) {
      setFallbackAddOpen(true)
      return
    }

    rooting.current = focusEquipmentId
    createRoot.mutate(
      { kind: 'UNIT', code, equipmentId: machine.id },
      { onSuccess: (node) => navigate(`/equipment/storage/${node.id}?setup=1`) },
    )
    // `createRoot` is a stable mutation object; including it would re-run this
    // on every mutation state change, which is what `rooting` guards anyway.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    focusEquipmentId,
    roots.data,
    roots.isSuccess,
    equipmentUnits.data,
    equipmentUnits.isSuccess,
    navigate,
    can,
  ])

  return (
    <div className='space-y-4'>
      {/* Rooting the unit from the Information dialog happens without a dialog
          of its own, so its failure has nowhere else to appear. A code already
          taken is the realistic one. */}
      {createRoot.isError && (
        <p className='text-sm text-destructive'>
          Could not set this machine up for storage: {createRoot.error.message}
        </p>
      )}
      {createRoot.isPending && (
        <p className='text-sm text-muted-foreground'>Setting this machine up for storage…</p>
      )}

      <StorageList
        unitHref={(row) => `/equipment/storage/${row.nodeId}`}
        receiveStock={false}
        onCopy={can('inventory.configure') ? (row) => setCopying(row) : undefined}
        onReplace={can('storage.replace') ? (row) => setReplacing(row) : undefined}
        onDelete={can('inventory.configure') ? (row) => setDeleting(row) : undefined}
      />

      <AddStorageUnitDialog
        open={fallbackAddOpen}
        onClose={() => setFallbackAddOpen(false)}
        rootedEquipmentIds={rootedEquipmentIds}
        onAdded={(id) => {
          setFallbackAddOpen(false)
          navigate(`/equipment/storage/${id}?setup=1`)
        }}
      />

      <CopyLayoutDialog
        sourceId={copying?.nodeId ?? null}
        sourceLabel={copying?.name ?? copying?.code ?? 'this unit'}
        onClose={() => setCopying(null)}
        onCopied={(targetId) => navigate(`/equipment/storage/${targetId}`)}
      />

      <DeleteStorageUnitDialog
        unitId={deleting?.nodeId ?? null}
        unitLabel={deleting?.name ?? deleting?.code ?? 'this unit'}
        onClose={() => setDeleting(null)}
      />

      {replacing && (
        <ReplaceUnitDialog
          open
          onClose={() => setReplacing(null)}
          sourceRootId={replacing.nodeId}
          sourceCode={replacing.code}
          onPlanned={(transfer) => navigate(`/inventory/storage-transfers/${transfer.id}`)}
        />
      )}
    </div>
  )
}
